import React, { useEffect, useState } from "react";
import { Typography, Autocomplete, TextField } from "@mui/material";
import { DashboardCard } from "../../../components/DashboardCard/DashboardCard";
import { DonutChart, DonutChartProps } from "../../../components/DonutChart/DonutChart";
import { useFleetPackingSummary } from "../../../hooks/PackingHealth/useFleetPackingSummary";
import FleetPackingsHealthGrid from "./PackingsHealthGrid/FleetPackingsHealthGrid";
import "./fleetPackingHealth.scss";
import {FramePackingData, Organization} from "../../../autogenerate/api.generated.clients";
import OrganizationsAutoComplete from "../../../components/OrganizationsAutoComplete/OrganizationsAutoComplete";
import {useUserParentOrganizations} from "../../../hooks/Organization/useUserParentOrganizations";
import {useGetIsArielEmployee} from "../../../hooks/Organization/useGetIsArielEmployee";


const FleetPackingHealth = () => {
    const [compressorOptions, setCompressorOptions] = useState<string[]>([]);
    const [selectedCompressors, setSelectedCompressors] = useState<string[]>([]);
    const [totalPackingsCount, setTotalPackingsCount] = useState<number | undefined>(undefined);
    const [averageLeakSeverity, setAverageLeakSeverity] = useState<string | undefined>(undefined);
    const [packingStatusCounts, setPackingStatusCounts] = useState<(number | undefined)[]>([]);
    const [framePackingData, setFramePackingData] = useState<FramePackingData[]>([] as FramePackingData[]);
    const [predictionFilter, setPredictionFilter] = useState<string | undefined>(undefined);
    
    const [subOrg, setSubOrg] = useState<Organization | null>(null);
    const fleetPackingHealthSummary = useFleetPackingSummary(subOrg?.externalId);
    const parentOrganizations = useUserParentOrganizations().data;
    const isArielEmployee = useGetIsArielEmployee().data;


    const packingStatusAndColorData = [
        { status: "Normal Operation", prediction: 'NormalOperation', color: "#497b37" },
        { status: "Leak Detected", prediction: 'LeakDetected', color: "#f9ae33" },
        { status: "Abnormal Temperature", prediction: 'AbnormalTemperature',  color: "#af3219" },
        { status: "Failed Instrumentation", prediction: 'FailedInstrumentation', color: "#6b7280" },
        { status: "Configuration Error", prediction: 'M_LinkConfigError', color: "#111827" }
    ];

    useEffect(() => {
        setDefaultValues();
    }, [fleetPackingHealthSummary.data]);

    useEffect(() => {
        if (selectedCompressors.length === 0) {
            setDefaultValues();
        } else {
            updateDataForSelectedCompressors();
        }
    }, [selectedCompressors]);

    const packingStatusData = packingStatusAndColorData.map((item, index) => ({
        ...item,
        count: packingStatusCounts[index] ?? 0
    }));

    const setDefaultValues = () => {
        if (fleetPackingHealthSummary.data) {
            const options = fleetPackingHealthSummary.data.framePackingData.map(frame => frame.frameSerialNumber);
            setCompressorOptions(options);
            setTotalPackingsCount(fleetPackingHealthSummary.data.packingCount);
            setAverageLeakSeverity(`${fleetPackingHealthSummary.data.averageLeakSeverity.toFixed(2)}%`);
            setPackingStatusCounts([
                fleetPackingHealthSummary.data.normalOperationCount,
                fleetPackingHealthSummary.data.leakDetectedCount,
                fleetPackingHealthSummary.data.abnormalTemperatureCount,
                fleetPackingHealthSummary.data.failedInstrumentationCount,
                fleetPackingHealthSummary.data.m_LinkConfigErrorCount
            ]);
            setFramePackingData(fleetPackingHealthSummary.data.framePackingData);
        }
    }
    
    const updateDataForSelectedCompressors = () => {
        const framePackingDataForSelectedFrames = fleetPackingHealthSummary.data?.framePackingData.filter(packingData => selectedCompressors.includes(packingData.frameSerialNumber));
        const totalPackingCountForSelectedFrames = framePackingDataForSelectedFrames?.reduce((acc, packingData) => acc + packingData.packingCount, 0);
        const averageLeakSeverityForSelectedFrames =
            framePackingDataForSelectedFrames ?
                framePackingDataForSelectedFrames.reduce((acc, packingData) => acc + packingData.averageLeakSeverity, 0) / framePackingDataForSelectedFrames.length
                :
                undefined;
        const totalNormalOperationCount = framePackingDataForSelectedFrames?.reduce((acc, packingData) => acc + packingData.normalOperationCount, 0) ?? 0;
        const totalLeakDetectedCount = framePackingDataForSelectedFrames?.reduce((acc, packingData) => acc + packingData.leakDetectedCount, 0) ?? 0;
        const totalAbnormalTemperatureCount = framePackingDataForSelectedFrames?.reduce((acc, packingData) => acc + packingData.abnormalTemperatureCount, 0) ?? 0;
        const totalFailedInstrumentationCount = framePackingDataForSelectedFrames?.reduce((acc, packingData) => acc + packingData.failedInstrumentationCount, 0) ?? 0;
        const totalConfigurationErrorCount = framePackingDataForSelectedFrames?.reduce((acc, packingData) => acc + packingData.m_LinkConfigErrorCount, 0) ?? 0;

        setTotalPackingsCount(totalPackingCountForSelectedFrames);
        setAverageLeakSeverity(`${averageLeakSeverityForSelectedFrames?.toFixed(2)}%`);
        setPackingStatusCounts([
            totalNormalOperationCount,
            totalLeakDetectedCount,
            totalAbnormalTemperatureCount,
            totalFailedInstrumentationCount,
            totalConfigurationErrorCount
        ]);
        setFramePackingData(framePackingDataForSelectedFrames ?? [] as FramePackingData[]);
    }
    
    const filterDataOnSegmentClick = (data: any) => {
        data.prediction == predictionFilter ? setPredictionFilter(undefined) : setPredictionFilter(data.prediction);
    };

    return (
        <div className={"fleet-packing-health"}>
            <div className={'report-filter'}>
                <div className={'compressor-filter'}>
                <Typography variant={"body1"} sx={{margin: '1em'}}>Showing packings for: </Typography>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    disablePortal
                    options={compressorOptions}
                    value={selectedCompressors}
                    onChange={(event, newValue) => {setSelectedCompressors(newValue)}}
                    sx={{maxWidth: '100%', backgroundColor: 'white', width: '70%'}}
                    renderInput={(params) => <TextField {...params} label="All Compressors" />}
                />
                </div>
                {isArielEmployee ?
                <div className="auto-complete-container">
                    <div className="auto-complete-width">
                        <OrganizationsAutoComplete
                            updateSelectedOrg={(selectedOrg: Organization) => setSubOrg(selectedOrg)}
                            parentOrganizations={parentOrganizations} selectedOrganization={subOrg}
                        />
                    </div>
                </div>
                : ''}
            </div>
            <div className={'dashboard-summary-cards'}>
                <DashboardCard label={"Total Packings"} value={totalPackingsCount ?? "undefined"} isLoading={fleetPackingHealthSummary.isLoading} />
                <DashboardCard label={"Average Leak Severity"} value={averageLeakSeverity ?? "undefined"} isLoading={fleetPackingHealthSummary.isLoading} />
            </div>
            <div className={'packing-status-card'} style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                <DonutChart data={packingStatusData} isLoading={fleetPackingHealthSummary.isLoading} onSegmentClick={filterDataOnSegmentClick} showLegend={true}/>
            </div>
            <div style={{flexGrow: 1, overflow: 'hidden'}}>
                <FleetPackingsHealthGrid data={framePackingData} isLoading={fleetPackingHealthSummary.isLoading} predictionFilter={predictionFilter} />
            </div>
        </div>
    )
}

export default FleetPackingHealth;